import React, { useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Shield, Menu, Building2, Users, LayoutDashboard,
  Bell, ChevronLeft, LogOut, Settings,
  Plug2, Mail, MessageSquare, Megaphone,
  ChevronDown, ExternalLink, FileText, Radio, ScrollText, UserSquare2,
  Activity, Bug, Flag, ChevronRight, Sparkles, Stethoscope
} from 'lucide-react';

// Navigation structure with groups
const navGroups = [
  {
    label: null, // No label for main items
    items: [
      { href: '/super-admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
      { href: '/super-admin/tenants', label: 'Leagues', icon: Building2 },
      { href: '/super-admin/users', label: 'Users', icon: Users },
    ]
  },
  {
    label: 'Communications',
    items: [
      { href: '/super-admin/comms', label: 'Overview', icon: Mail, exact: true },
      { href: '/super-admin/comms/notifications', label: 'Notifications', icon: Bell },
      { href: '/super-admin/comms/templates', label: 'Templates', icon: FileText },
      { href: '/super-admin/comms/channels', label: 'Channels', icon: Radio },
      { href: '/super-admin/comms/logs', label: 'Logs', icon: ScrollText },
    ]
  },
  {
    label: 'Platform',
    items: [
      { href: '/super-admin/user-logs', label: 'User Activity', icon: Activity },
      { href: '/super-admin/issues-log', label: 'Issues Log', icon: Bug },
      { href: '/super-admin/scoring-diagnostics', label: 'Scoring Diagnostics', icon: Stethoscope },
      { href: '/super-admin/default-contestants', label: 'Default Contestants', icon: UserSquare2 },
      { href: '/super-admin/announcements', label: 'Announcements', icon: Megaphone },
      { href: '/super-admin/integrations', label: 'Integrations', icon: Plug2 },
      { href: '/super-admin/beta-features', label: 'Beta Features', icon: Sparkles },
      { 
        href: '/super-admin/settings', 
        label: 'Settings', 
        icon: Settings,
        submenu: [
          { href: '/super-admin/settings', label: 'General', icon: Settings, exact: true },
          { href: '/super-admin/settings/feature-flags', label: 'Feature Flags', icon: Flag },
        ]
      },
    ]
  }
];

export default function SuperAdminLayout({ children, backLink }) {
  const { user, logout } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(() => {
    // Auto-expand Settings menu if we're on a settings page
    if (location.pathname.startsWith('/super-admin/settings')) {
      return ['Settings'];
    }
    return [];
  });

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const toggleSubmenu = (label) => {
    setExpandedMenus(prev => 
      prev.includes(label) 
        ? prev.filter(l => l !== label)
        : [...prev, label]
    );
  };

  // Sidebar content - shared between desktop and mobile
  const SidebarContent = ({ onItemClick }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-zinc-800">
        <Link to="/super-admin" className="flex items-center gap-3" onClick={onItemClick}>
          <div className="w-9 h-9 bg-amber-500 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-zinc-900" />
          </div>
          <div>
            <span className="font-semibold text-zinc-100 text-sm">Super Admin</span>
            <span className="text-zinc-500 text-xs block">Platform Control</span>
          </div>
        </Link>
      </div>
      
      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="px-3 space-y-6">
          {navGroups.map((group, groupIndex) => (
            <div key={groupIndex}>
              {group.label && (
                <h3 className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                  {group.label}
                </h3>
              )}
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const hasSubmenu = item.submenu && item.submenu.length > 0;
                  const isExpanded = expandedMenus.includes(item.label);
                  const isAnySubmenuActive = hasSubmenu && item.submenu.some(sub => isActive(sub.href, sub.exact));
                  const active = isActive(item.href, item.exact) || isAnySubmenuActive;
                  
                  if (hasSubmenu) {
                    return (
                      <div key={item.href}>
                        {/* Parent menu item with submenu */}
                        <button
                          onClick={() => toggleSubmenu(item.label)}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            active 
                              ? 'bg-amber-500/10 text-amber-500' 
                              : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-100'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="w-4 h-4 flex-shrink-0" />
                            <span>{item.label}</span>
                          </div>
                          <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </button>
                        
                        {/* Submenu items */}
                        {isExpanded && (
                          <div className="ml-4 mt-1 space-y-1 border-l border-zinc-800 pl-3">
                            {item.submenu.map((subItem) => {
                              const SubIcon = subItem.icon;
                              const subActive = isActive(subItem.href, subItem.exact);
                              return (
                                <Link
                                  key={subItem.href}
                                  to={subItem.href}
                                  onClick={onItemClick}
                                  data-testid={`nav-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                    subActive 
                                      ? 'bg-amber-500/10 text-amber-500' 
                                      : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-100'
                                  }`}
                                >
                                  <SubIcon className="w-4 h-4 flex-shrink-0" />
                                  <span>{subItem.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  // Regular menu item without submenu
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={onItemClick}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        active 
                          ? 'bg-amber-500/10 text-amber-500' 
                          : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-100'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>
      
      {/* Footer */}
      <div className="p-3 border-t border-zinc-800">
        <Link
          to="/home"
          onClick={onItemClick}
          data-testid="nav-back-to-app"
          className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-zinc-500 hover:bg-zinc-800/50 hover:text-zinc-300 transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          <span>Back to App</span>
        </Link>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-zinc-950 text-zinc-100">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-60 flex-col border-r border-zinc-800 bg-zinc-900/50">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar (Sheet) */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-zinc-900 border-zinc-800">
          <SidebarContent onItemClick={() => setMobileMenuOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header Bar */}
        <header className="h-14 border-b border-zinc-800 bg-zinc-900/50 flex items-center justify-between px-4">
          {/* Left side - Mobile menu + Breadcrumb */}
          <div className="flex items-center gap-3">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="md:hidden text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 -ml-2"
                  data-testid="mobile-menu-toggle"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
            </Sheet>
            
            {backLink && (
              <Link to={backLink}>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800 gap-1"
                  data-testid="back-button"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Back</span>
                </Button>
              </Link>
            )}
          </div>

          {/* Right side - User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="flex items-center gap-2 hover:bg-zinc-800 px-2 py-1.5 rounded-lg transition-colors"
                data-testid="user-menu-trigger"
              >
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-zinc-900 font-semibold text-sm">
                  {user?.name?.[0]?.toUpperCase() || 'A'}
                </div>
                <span className="hidden sm:block text-sm text-zinc-300 max-w-32 truncate">
                  {user?.name || 'Admin'}
                </span>
                <ChevronDown className="w-4 h-4 text-zinc-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-800">
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-zinc-100">{user?.name}</p>
                <p className="text-xs text-zinc-500">{user?.email}</p>
                <span className="inline-block mt-1 px-1.5 py-0.5 bg-amber-500/20 text-amber-500 text-[10px] font-medium rounded">
                  Super Admin
                </span>
              </div>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem 
                onClick={() => navigate('/home')}
                className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-100"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Back to App
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem 
                onClick={logout}
                className="text-red-400 focus:bg-red-500/10 focus:text-red-400"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-6 lg:p-8 max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
